
import { useState, useEffect } from 'react';
import { waitForHydrationComplete } from '@/utils/hydrationManager';

/**
 * SSR-safe hook that uses CSS Media Queries via matchMedia API
 * This avoids forced reflows by not reading layout properties directly
 * 
 * 🛡️ SYNC FIX: Uses waitForHydrationComplete() instead of setTimeout(0)
 * to ensure the same deterministic gate as HydrationManager
 * 
 * @param query CSS media query string (e.g., "(max-width: 1023px)")
 * @returns boolean indicating if the media query matches
 */
export function useMediaQuery(query: string): boolean {
  // Default to false for SSR - consistent with mobile-first approach
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    // SSR check
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    let cancelled = false;
    
    // 🛡️ Wait for hydration gate before updating state
    // This ensures we don't trigger setState during React hydration
    waitForHydrationComplete().then(() => {
      // 🛡️ Extra rAF to allow Suspense boundaries to complete hydration
      // Fixes React #421 error on pages with lazy-loaded charts
      requestAnimationFrame(() => {
        if (!cancelled) {
          setMatches(mediaQuery.matches);
        }
      });
    });

    // Handler for media query changes (safe post-hydration)
    const handleChange = (event: MediaQueryListEvent) => {
      if (!cancelled) {
        setMatches(event.matches);
      }
    };

    // Modern API - addEventListener
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      cancelled = true;
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

/**
 * Common media query constants
 * Using these ensures consistency across the app
 */
export const MEDIA_QUERIES = {
  // Mobile breakpoint < 1024px
  MOBILE: '(max-width: 1023px)',
  // Tablet breakpoint 768-1023px
  TABLET: '(min-width: 768px) and (max-width: 1023px)',
  // Desktop breakpoint >= 1024px
  DESKTOP: '(min-width: 1024px)',
  // Small screen for charts < 768px
  SMALL_SCREEN: '(max-width: 767px)',
  // Landscape orientation
  LANDSCAPE: '(orientation: landscape)',
  // Mobile landscape (small height in landscape)
  MOBILE_LANDSCAPE: '(max-width: 1023px) and (orientation: landscape) and (max-height: 500px)',
  // Reduced motion preference
  REDUCED_MOTION: '(prefers-reduced-motion: reduce)',
} as const;
